// App.jsx
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import Navbar from "./components/HomePage/Navbar";
import Footer from "./components/HomePage/Footer";
import WhatsAppButton from "./components/common/WhatsAppButton";
import Home from "./pages/Home";

import "./App.css";

// Everything except the homepage is code-split, so a first visit to "/"
// no longer downloads the About, Portfolio, Blog, Contact and 25 service
// detail pages it will probably never show.
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

const ServiceDetailDigTrans = lazy(() =>
  import("./pages/Services/DigitalTransformation/ServiceDetail_DigTrans")
);
const ServiceDetailEmergTech = lazy(() =>
  import("./pages/Services/EmergingTech/ServiceDetail_EmergTech")
);
const ServiceDetailBussApp = lazy(() =>
  import("./pages/Services/BussinessApp/ServiceDetail_BussApp")
);
const ServiceDetailDigiM = lazy(() =>
  import("./pages/Services/DigitalMarketing/ServiceDetail_DigiM")
);

/**
 * Reset scroll on navigation.
 *
 * Uses an instant jump rather than smooth scrolling: a smooth scroll on
 * route change animates past the whole outgoing page, which reads as a
 * lag before the new page appears. Visitors who have asked for reduced
 * motion get the same instant behaviour.
 */
function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  React.useEffect(() => {
    if (hash) return; // let in-page anchors resolve normally
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, search, hash]);

  return null;
}

/** Lightweight placeholder while a route chunk loads. */
function RouteFallback() {
  return (
    <div
      className="flex min-h-[70vh] items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading</span>
      <span className="relative flex h-10 w-10">
        <span className="absolute inset-0 rounded-full border-2 border-brand-100" />
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-brand-500" />
      </span>
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />

        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-brand-800 focus:px-4 focus:py-2 focus:text-white"
        >
          Skip to content
        </a>

        <Navbar />

        <main id="main">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/contact" element={<Contact />} />

              {/* Existing, indexed service URLs — paths unchanged. */}
              <Route
                path="/services/DigitalTransformation/:slug"
                element={<ServiceDetailDigTrans />}
              />
              <Route
                path="/services/EmergingTech/:slug"
                element={<ServiceDetailEmergTech />}
              />
              <Route
                path="/services/BussinessApp/:slug"
                element={<ServiceDetailBussApp />}
              />
              <Route
                path="/services/BusinessApp/:slug"
                element={<ServiceDetailBussApp />}
              />
              <Route
                path="/services/DigitalMarketing/:slug"
                element={<ServiceDetailDigiM />}
              />

              {/* Previously these rendered a blank page with a 200 status. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
        <WhatsAppButton />
      </div>
    </Router>
  );
}

export default App;
